import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Bats that fly across the screen
const BATS = Array.from({ length: 7 }).map((_, i) => ({
  id: i,
  top: 8 + Math.random() * 55,
  delay: Math.random() * 0.9,
  duration: 1.8 + Math.random() * 1.2,
  size: 26 + Math.random() * 26,
  flip: i % 2 === 0,
}));

export const IntroSplash = () => {
  const [show, setShow] = useState(() => !sessionStorage.getItem("stb_intro_hw"));
  const [rise, setRise] = useState(false);

  useEffect(() => {
    if (!show) return;
    sessionStorage.setItem("stb_intro_hw", "1");
    const t1 = setTimeout(() => setRise(true), 650);
    const t2 = setTimeout(() => setShow(false), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden cursor-pointer"
          style={{ background: "radial-gradient(120% 90% at 50% 10%, #3b0764 0%, #1a0330 45%, #000 100%)" }}
          data-testid="intro-splash"
          onClick={() => setShow(false)}
        >
          {/* Moon glow */}
          <div
            className="absolute rounded-full"
            style={{
              top: "8%", right: "16%", width: 150, height: 150,
              background: "radial-gradient(circle, #FFE9A8 0%, #FFC94D 55%, rgba(255,201,77,0) 72%)",
              filter: "blur(2px)",
            }}
          />

          {/* Drifting fog layers */}
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute left-0 right-0 pointer-events-none"
              style={{
                bottom: `${i * 14}%`,
                height: 220,
                background: "radial-gradient(60% 100% at 50% 100%, rgba(255,255,255,0.16) 0%, rgba(200,180,255,0.05) 50%, transparent 75%)",
                filter: "blur(18px)",
              }}
              initial={{ x: i % 2 === 0 ? "-30%" : "30%", opacity: 0 }}
              animate={{ x: i % 2 === 0 ? "30%" : "-30%", opacity: [0, 0.9, 0.9] }}
              transition={{ duration: 6, repeat: Infinity, repeatType: "reverse", ease: "easeInOut", delay: i * 0.4 }}
            />
          ))}

          {/* Flying bats */}
          {BATS.map((b) => (
            <motion.span
              key={b.id}
              className="absolute select-none"
              style={{ top: `${b.top}%`, fontSize: b.size, transform: b.flip ? "scaleX(-1)" : "none", filter: "drop-shadow(0 0 6px rgba(0,0,0,0.6))" }}
              initial={{ x: "-15vw", opacity: 0 }}
              animate={{ x: "115vw", opacity: [0, 1, 1, 0.9], y: [0, -22, 8, -14, 0] }}
              transition={{ duration: b.duration, delay: b.delay, ease: "easeInOut" }}
              aria-hidden
            >
              🦇
            </motion.span>
          ))}

          {/* Tombstone rising */}
          <motion.div
            className="relative z-[205] flex flex-col items-center"
            initial={{ y: 260, opacity: 0, rotate: -2 }}
            animate={rise ? { y: 0, opacity: 1, rotate: 0 } : {}}
            transition={{ type: "spring", stiffness: 90, damping: 15 }}
          >
            <div
              className="relative px-8 pt-10 pb-8 text-center"
              style={{
                width: "min(78vw, 380px)",
                background: "linear-gradient(180deg,#8b8f96 0%,#6b7078 55%,#4b5058 100%)",
                borderTopLeftRadius: "50% 42%",
                borderTopRightRadius: "50% 42%",
                border: "5px solid #2b2e33",
                boxShadow: "10px 12px 0 rgba(0,0,0,0.45), inset 0 6px 14px rgba(255,255,255,0.25)",
              }}
            >
              <div className="font-mono text-[11px] font-bold tracking-[0.35em] text-black/55">R.I.P. BOREDOM</div>
              <div className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight text-[#1c1f24] mt-2 drop-shadow-[1px_1px_0_rgba(255,255,255,0.35)]">
                THE SWEET<br />TOOTH BANDIT
              </div>
              <div className="mt-3 h-[3px] w-16 mx-auto bg-black/40 rounded-full" />
            </div>
            {/* dirt mound */}
            <div
              className="h-6 -mt-1"
              style={{ width: "min(88vw, 440px)", background: "linear-gradient(180deg,#2e1a12 0%,#160b07 100%)", borderRadius: "50%", filter: "blur(1px)" }}
            />
          </motion.div>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: rise ? [0, 1, 0.6, 1] : 0, y: rise ? 0 : 16 }}
            transition={{ duration: 1.2, delay: 0.5, times: [0, 0.3, 0.6, 1] }}
            className="absolute bottom-16 z-[210] font-display text-3xl sm:text-5xl tracking-tight text-center px-4"
            style={{ color: "#FF7518", textShadow: "0 0 18px rgba(255,117,24,0.7), 3px 3px 0 #000" }}
          >
            CALLING ALL BANDITS <span aria-hidden>🎃</span>
          </motion.p>

          <button
            data-testid="skip-intro-btn"
            onClick={(e) => { e.stopPropagation(); setShow(false); }}
            className="absolute top-6 right-6 z-[220] font-mono text-xs font-bold uppercase tracking-widest bg-white text-black border-4 border-black rounded-full px-4 py-2 shadow-brutal-sm"
          >
            Skip ✕
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
